var _excluded = ["in", "unmountOnExit", "animateOpacity", "disableMotion", "duration", "style", "className", "children", "onExited"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import * as React from 'react';
import { Transition } from 'react-transition-group';
import { useDebounceCallback } from '@react-hook/debounce';
import { tqc } from '../../system';
import { cx, isFunction, __DEV__ } from '../../utils';
var defaultOptions = {
  duration: 200
};
var getSharedStyles = function getSharedStyles(elementHeight) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOptions;
  var {
    duration,
    disableMotion
  } = options;
  return {
    opacity: 1,
    height: elementHeight,
    transitionProperty: disableMotion ? 'none' : 'height, opacity',
    transitionTimingFunction: 'ease',
    transitionDuration: "".concat(duration, "ms")
  };
};
var getTransitionStyles = function getTransitionStyles(elementHeight) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOptions;
  var {
    animateOpacity
  } = options;
  return {
    entering: _objectSpread(_objectSpread({}, animateOpacity && {
      opacity: 1
    }), {}, {
      height: elementHeight
    }),
    entered: _objectSpread(_objectSpread({}, animateOpacity && {
      opacity: 1
    }), {}, {
      height: elementHeight
    }),
    exiting: _objectSpread(_objectSpread({}, animateOpacity && {
      opacity: 0
    }), {}, {
      height: '0'
    }),
    exited: _objectSpread(_objectSpread({}, animateOpacity && {
      opacity: 0
    }), {}, {
      height: '0'
    })
  };
};
var getStyles = (status, elementHeight, options) => {
  return _objectSpread(_objectSpread({}, getSharedStyles(elementHeight, options)), getTransitionStyles(elementHeight, options)[status]);
};
export var getCollapseStyles = getStyles;
export var getCollapseTransitionStyles = getTransitionStyles;
export var getCollapseSharedStyles = getSharedStyles;
export var Collapse = /*#__PURE__*/React.forwardRef((props, ref) => {
  var {
      in: isOpen,
      unmountOnExit,
      animateOpacity = true,
      disableMotion = false,
      duration = defaultOptions.duration,
      style,
      className,
      children,
      onExited
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var elementRef = React.useRef();
  /**
   * Height of collapse element. Used to animate the height
   * of collapse element when it is toggled open / close.
   */
  var collapsePanelHeightRef = React.useRef('0px');
  var options = React.useMemo(() => ({
    animateOpacity,
    duration,
    disableMotion
  }), [animateOpacity, duration, disableMotion]);
  var ownProps = React.useMemo(() => _objectSpread(_objectSpread({
    ref,
    onExited,
    className: cx('tqc-collapse', className)
  }, rest), {}, {
    style: _objectSpread({
      overflow: 'visible'
    }, style)
  }), [className, onExited, ref, rest, style]);
  var _children = React.useMemo(() => {
    var getTransitionStyles = status => _objectSpread(_objectSpread({}, ownProps.style), getStyles(status, collapsePanelHeightRef.current, options));
    return isFunction(children) ? status => children({
      transitionStyles: getTransitionStyles(status),
      transitionStatus: status
    }) : status => /*#__PURE__*/React.createElement(tqc.div, _extends({
      ref: ref,
      className: cx('tqc-collapse', className),
      __css: getTransitionStyles(status)
    }, rest), children);
  }, [children, className, ownProps, options, ref, rest]);
  var onResize = React.useCallback(() => {
    var {
      current: element
    } = elementRef;
    if (!element) return;
    collapsePanelHeightRef.current = element.scrollHeight + 'px';
  }, []);
  var onResizeDebounced = useDebounceCallback(onResize, 30);
  React.useEffect(() => {
    window.addEventListener('resize', onResizeDebounced);
    return () => {
      window.removeEventListener('resize', onResizeDebounced);
    };
  }, [onResizeDebounced]);
  return /*#__PURE__*/React.createElement(Transition, {
    in: isOpen,
    timeout: duration,
    unmountOnExit: unmountOnExit,
    onEnter: element => {
      elementRef.current = element;
      collapsePanelHeightRef.current = element.scrollHeight + 'px';
      element.style.height = '';
    },
    onEntered: element => {
      element.style.height = 'auto';
    },
    onExit: element => {
      collapsePanelHeightRef.current = element.scrollHeight + 'px';
      element.style.height = '';
      // Force a reflow
      element.getBoundingClientRect();
    }
  }, _children);
});
if (__DEV__) {
  Collapse.displayName = 'Collapse';
}